import React, { useEffect, useRef, useState } from 'react';
import { ZegoExpressEngine } from 'zego-express-engine-webrtc';
import { getSocket } from '../../socket';
import { FiMicOff, FiMic, FiVideo, FiPhoneCall } from 'react-icons/fi';
import { IoCall } from 'react-icons/io5';
import { FaVideo, FaVideoSlash } from 'react-icons/fa';

const getRegisterUser = () => {
  try {
    const localData = localStorage.getItem("Register_User");
    return localData ? JSON.parse(localData) : null;
  } catch (e) {
    return null;
  }
};

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
  const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${mins}:${secs}`;
};

export default function ZegoCloudHandler({
  channel,
  isVideo,
  isCaller,
  receiverId,
  receiverName,
  receiverPic,
  credentials,
  token: tokenProp,
  onClose
}) {
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);

  const [micOn, setMicOn] = useState(true);
  const [cameraOn, setCameraOn] = useState(isVideo);
  const [accepted, setAccepted] = useState(!isCaller);
  const [connected, setConnected] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Timer: Start ONLY when accepted AND connected
  useEffect(() => {
    if (!accepted || !connected) {
      setElapsedSeconds(0);
      return undefined;
    }
    const interval = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [accepted, connected]);

  useEffect(() => {
    let zgInstance = null;
    let localMediaStream = null;

    const startZegoEngine = async () => {
      try {
        const appId = Number(credentials?.app_id || 0);
        const serverSecret = credentials?.server_secret || credentials?.app_sign || '';
        if (!appId) {
          console.warn('[ZEGO] App ID missing from credentials');
          return;
        }

        const user = getRegisterUser() || {};
        const userID = String(user.id || user.uid || Date.now());
        const userName = String(user.name || user.email || 'WebUser');

        console.log('[ZEGO] Initializing ZegoExpressEngine with appId:', appId, 'serverSecret:', serverSecret ? 'YES' : 'NO', 'channel:', channel, 'userID:', userID);
        zgInstance = new ZegoExpressEngine(appId, 'wss://web-express.zego.im/ws');

        zgInstance.on('roomStateUpdate', (roomID, state, errorCode, extendedData) => {
          console.log('[ZEGO] roomStateUpdate:', roomID, state, errorCode, extendedData);
          if (state === 'CONNECTED') {
            setConnected(true);
          }
        });

        zgInstance.on('publisherStateUpdate', (result) => {
          console.log('[ZEGO] publisherStateUpdate:', result);
          if (result.state === 'PUBLISHING') {
            setConnected(true);
          }
        });

        zgInstance.on('playerStateUpdate', (result) => {
          console.log('[ZEGO] playerStateUpdate:', result);
          if (result.state === 'PLAYING') {
            setConnected(true);
          }
        });

        zgInstance.on('roomUserUpdate', (roomID, updateType, userList) => {
          console.log('[ZEGO] roomUserUpdate:', roomID, updateType, userList);
          if (updateType === 'ADD') {
            setConnected(true);
          }
        });

        zgInstance.on('roomStreamUpdate', async (roomID, updateType, streamList) => {
          console.log('[ZEGO] roomStreamUpdate event:', updateType, streamList);
          if (updateType === 'ADD') {
            for (const streamInfo of streamList) {
              try {
                const remoteStream = await zgInstance.startPlayingStream(streamInfo.streamID);
                console.log('[ZEGO] Remote stream playing:', streamInfo.streamID, 'audioTracks:', remoteStream?.getAudioTracks()?.length);
                if (remoteStream) {
                  remoteStream.getAudioTracks().forEach(track => {
                    track.enabled = true;
                  });
                }
                if (remoteVideoRef.current && isVideo) {
                  remoteVideoRef.current.srcObject = remoteStream;
                }
                if (remoteAudioRef.current) {
                  remoteAudioRef.current.srcObject = remoteStream;
                  remoteAudioRef.current.muted = false;
                  remoteAudioRef.current.volume = 1.0;
                  const playPromise = remoteAudioRef.current.play();
                  if (playPromise !== undefined) {
                    playPromise.catch(e => console.log('[ZEGO] Audio play catch:', e));
                  }
                }
                setConnected(true);
              } catch (e) {
                console.error('[ZEGO] Error playing stream:', e);
              }
            }
          } else if (updateType === 'DELETE') {
            for (const streamInfo of streamList) {
              zgInstance.stopPlayingStream(streamInfo.streamID);
            }
          }
        });

        const token = credentials?.token || credentials?.zegoToken || tokenProp || '';
        console.log('[ZEGO] Logging into room:', channel, 'with token length:', token.length);

        await zgInstance.loginRoom(channel, token, { userID, userName }, { maxMemberCount: 2 });
        console.log('[ZEGO] loginRoom SUCCESS');

        try {
          localMediaStream = await zgInstance.createStream({
            camera: { audio: true, video: !!isVideo }
          });
        } catch (e1) {
          console.warn('[ZEGO] createStream option 1 failed, trying option 2:', e1);
          try {
            localMediaStream = await zgInstance.createStream({
              audio: true,
              video: !!isVideo
            });
          } catch (e2) {
            console.error('[ZEGO] createStream option 2 failed:', e2);
          }
        }

        if (localMediaStream) {
          if (localVideoRef.current && isVideo) {
            localVideoRef.current.srcObject = localMediaStream;
          }

          const myStreamId = `${channel}_${userID}`;
          await zgInstance.startPublishingStream(myStreamId, localMediaStream);
          console.log('[ZEGO] startPublishingStream SUCCESS:', myStreamId);
        }

      } catch (err) {
        console.error('[ZEGO] Engine init error:', err);
      }
    };

    startZegoEngine();

    return () => {
      if (zgInstance) {
        try {
          const user = getRegisterUser() || {};
          const userID = String(user.id || user.uid || Date.now());
          zgInstance.stopPublishingStream(`${channel}_${userID}`);
          if (localMediaStream) {
            zgInstance.destroyStream(localMediaStream);
          }
          zgInstance.logoutRoom(channel);
        } catch (e) {
          console.error('[ZEGO] Cleanup error:', e);
        }
      }
    };
  }, [channel, credentials, isVideo, tokenProp]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handleCallAccepted = (data) => {
      if (!data?.channel || String(data.channel) === String(channel)) {
        setAccepted(true);
      }
    };

    const handleCallEnded = (data) => {
      if (!data?.channel || String(data.channel) === String(channel)) {
        onClose();
      }
    };

    socket.on('call_accepted', handleCallAccepted);
    socket.on('call_ended', handleCallEnded);
    socket.on('call_rejected', handleCallEnded);

    return () => {
      socket.off('call_accepted', handleCallAccepted);
      socket.off('call_ended', handleCallEnded);
      socket.off('call_rejected', handleCallEnded);
    };
  }, [channel, onClose]);

  const leaveCall = () => {
    const socket = getSocket();
    if (socket?.connected && channel) {
      socket.emit('call_end', {
        otherId: receiverId,
        channel,
        status: 'completed',
        reason: 'ended_by_user'
      });
    }
    onClose();
  };

  const statusText = !accepted
    ? (isCaller ? "Ringing..." : "Connecting...")
    : !connected
      ? "Connecting..."
      : "Connected";

  const controlButtonClass = "flex h-14 w-14 items-center justify-center rounded-full text-white shadow-xl transition duration-200 hover:-translate-y-0.5";

  if (isVideo) {
    return (
      <div className="relative h-[min(720px,92vh)] min-h-[600px] w-full overflow-hidden rounded-[28px] bg-black text-white shadow-2xl">
        <audio ref={remoteAudioRef} autoPlay playsInline controls={false} className="fixed top-0 left-0 w-1 h-1 opacity-0 pointer-events-none" />
        <div className="absolute inset-0 z-0 bg-gray-950 flex items-center justify-center">
          <video ref={remoteVideoRef} autoPlay playsInline className={`h-full w-full object-cover ${accepted && connected ? "block" : "hidden"}`} />
          {(!accepted || !connected) && (
            <div className="flex flex-col items-center">
              <img src={receiverPic} alt={receiverName} className="h-32 w-32 rounded-full border-[4px] border-white/20 object-cover blur-sm opacity-50" />
            </div>
          )}
        </div>

        <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-b from-black/35 via-transparent to-black/65" />

        <div className="absolute left-5 top-5 z-20 h-36 w-28 overflow-hidden rounded-2xl border border-white/30 bg-black/30 shadow-2xl">
          {cameraOn ? (
            <video ref={localVideoRef} autoPlay playsInline muted className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-900">
              <FaVideoSlash className="text-xl text-white/50" />
            </div>
          )}
        </div>

        <div className="pointer-events-none absolute left-1/2 top-5 z-20 -translate-x-1/2 rounded-full border border-white/20 bg-black/35 px-4 py-2 text-sm font-semibold backdrop-blur-md">
          <span className="inline-flex items-center gap-2">
            <FiVideo />
            Video call (ZegoCloud)
          </span>
        </div>

        <div className="pointer-events-none absolute left-1/2 top-20 z-20 -translate-x-1/2 rounded-2xl bg-black/35 px-5 py-3 text-center shadow-xl backdrop-blur-md">
          <h2 className="m-0 max-w-[220px] truncate text-2xl font-bold">{receiverName}</h2>
          <p className="m-0 mt-1 text-sm font-semibold text-white/80">
            {accepted && connected ? formatDuration(elapsedSeconds) : statusText}
          </p>
        </div>

        <div className="absolute bottom-6 left-1/2 z-30 flex w-[calc(100%-32px)] max-w-[390px] -translate-x-1/2 items-center justify-between rounded-[28px] bg-black/55 px-8 py-5 shadow-2xl backdrop-blur-xl">
          <button
            type="button"
            title={micOn ? "Mute microphone" : "Unmute microphone"}
            className={`${controlButtonClass} ${micOn ? "bg-black/70" : "bg-white/25"}`}
            onClick={() => setMicOn(!micOn)}
          >
            {micOn ? <FiMic className="text-2xl" /> : <FiMicOff className="text-2xl" />}
          </button>
          <button
            type="button"
            title="End call"
            className="flex h-16 w-16 items-center justify-center rounded-full bg-[#ff3f64] text-white shadow-[0_0_30px_rgba(255,63,100,0.45)] transition duration-200 hover:-translate-y-0.5 hover:bg-[#ff244f]"
            onClick={leaveCall}
          >
            <IoCall className="rotate-[135deg] text-3xl" />
          </button>
          <button
            type="button"
            title={cameraOn ? "Turn camera off" : "Turn camera on"}
            className={`${controlButtonClass} ${cameraOn ? "bg-black/70" : "bg-white/25"}`}
            onClick={() => setCameraOn(!cameraOn)}
          >
            {cameraOn ? <FaVideo className="text-xl" /> : <FaVideoSlash className="text-xl" />}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-[min(660px,90vh)] min-h-[540px] w-full overflow-hidden rounded-[30px] bg-[#190028] text-white shadow-2xl">
      <audio ref={remoteAudioRef} autoPlay playsInline controls={false} className="fixed top-0 left-0 w-1 h-1 opacity-0 pointer-events-none" />
      <div
        className="absolute inset-0 scale-110 bg-cover bg-center opacity-25 blur-[16px]"
        style={{ backgroundImage: `url(${receiverPic})` }}
      />
      <div className="absolute inset-0 bg-[linear-gradient(160deg,rgba(171,34,255,0.95)_0%,rgba(117,21,208,0.96)_42%,rgba(37,6,58,0.98)_100%)]" />
      <div className="absolute left-1/2 top-24 h-56 w-56 -translate-x-1/2 rounded-full bg-white/12 blur-3xl" />
      <div className="absolute -left-16 bottom-20 h-56 w-56 rounded-full bg-[#ff4f8b]/20 blur-3xl" />

      <div className="relative z-[2] flex h-full flex-col items-center justify-between px-6 py-8">
        <div className="rounded-full bg-white/14 px-4 py-2 text-sm font-semibold text-white shadow-lg backdrop-blur-md">
          <span className="inline-flex items-center gap-2">
            <FiPhoneCall />
            Audio call (ZegoCloud)
          </span>
        </div>

        <div className="flex flex-col items-center text-center">
          <div className="relative mb-8">
            <div className="absolute inset-[-18px] animate-pulse rounded-full bg-white/18 blur-md" />
            <div className="absolute inset-[-34px] rounded-full border border-white/10" />
            <img
              src={receiverPic}
              alt={receiverName || "Caller"}
              className="relative h-36 w-36 rounded-full border-[5px] border-white object-cover shadow-2xl"
            />
          </div>
          <h2 className="m-0 max-w-[220px] truncate text-4xl font-bold leading-tight">{receiverName}</h2>
          <p className="m-0 mt-2 text-sm font-medium text-white/75">{statusText}</p>
          <p className="m-0 mt-4 rounded-full bg-black/22 px-5 py-2 text-sm font-semibold tracking-wide text-white/90 shadow-inner">
            {formatDuration(elapsedSeconds)}
          </p>
        </div>

        <div className="flex w-full max-w-[340px] items-center justify-between rounded-[30px] bg-black/42 px-9 py-5 shadow-[0_24px_60px_rgba(0,0,0,0.28)] backdrop-blur-xl">
          <button
            type="button"
            onClick={() => setMicOn(!micOn)}
            title={micOn ? "Mute microphone" : "Unmute microphone"}
            className={`group flex h-16 w-16 items-center justify-center rounded-full transition duration-200 hover:-translate-y-0.5 hover:shadow-xl ${micOn ? "bg-black/70 text-white" : "bg-white text-[#190028]"}`}
          >
            {micOn ? <FiMic className="text-2xl" /> : <FiMicOff className="text-2xl" />}
          </button>
          <button
            type="button"
            onClick={leaveCall}
            title="End call"
            className="flex h-16 w-16 items-center justify-center rounded-full bg-[#ff3f64] text-white shadow-[0_0_30px_rgba(255,63,100,0.45)] transition duration-200 hover:-translate-y-0.5 hover:bg-[#ff244f]"
          >
            <IoCall className="rotate-[135deg] text-3xl" />
          </button>
        </div>
      </div>
    </div>
  );
}
